// @flow
import React from 'react'
import { Card, Grid, Typography } from '@material-ui/core'
import { isEmpty } from 'lodash/lang'
import { filter, each } from 'lodash/collection'
import { isEmail } from 'validator'
import i18n from 'i18next'

import AddressInvoiceForm from '../../../../../../account/personal/components/AddressInvoiceForm'
import styles from './styles'
import Button from '../../../../../../master/components/styled/ButtonBase'

type FormField = {
  value: string,
  isRequired?: boolean,
  error?: boolean,
  isValid?: Function
}

type State = {
  wantsInvoiceLegal: boolean,
  wantsInvoiceNatural: boolean,
  formFields: {
    invoiceEmail: FormField,
    invoiceFirstSurname: FormField,
    invoiceName: FormField,
    invoicePersonType: FormField,
    invoiceSecondSurname: FormField,
    nif: FormField,
    company: FormField
  }
}

type Props = {
  address: Object,
  hasInvoice: boolean,
  navigateTo: Function,
  toggleInvoiceID: Function,
  updateInvoiceData: Function
}

export class AddAddressInvoiceForm extends React.Component<Props, State> {
  state = {
    wantsInvoiceLegal: false,
    wantsInvoiceNatural: false,
    formFields: {
      company: {
        error: false,
        isRequired: false,
        isValid: (company: string) => !isEmpty(company),
        value: ''
      },
      invoiceEmail: {
        error: false,
        isRequired: false,
        isValid: (invoiceEmail: string) => isEmail(invoiceEmail),
        value: ''
      },
      invoiceFirstSurname: {
        error: false,
        isRequired: false,
        isValid: (invoiceFirstSurname: string) => !isEmpty(invoiceFirstSurname),
        value: ''
      },
      invoiceName: {
        error: false,
        isRequired: false,
        isValid: (invoiceName: string) => !isEmpty(invoiceName),
        value: ''
      },
      invoicePersonType: {
        error: false,
        isRequired: true,
        isValid: (invoicePersonType: string) => !isEmpty(invoicePersonType),
        value: ''
      },
      invoiceSecondSurname: {
        error: false,
        isRequired: false,
        isValid: (invoiceSecondSurname: string) =>
          !isEmpty(invoiceSecondSurname),
        value: ''
      },
      nif: {
        error: false,
        isRequired: false,
        isValid: (nif: string) => !isEmpty(nif),
        value: ''
      }
    }
  }

  onInvoicePersonTypeChange: Function = (event: Object) => {
    const mapFormFieldValueToState = (name: string, value: any) => ({
      ...this.state.formFields,
      [name]: { ...this.state.formFields[name], value }
    })

    const setRequiredFields = (requiredFields: Array<string>) => {
      return requiredFields.reduce(
        (memo, field) => ({
          ...memo,
          [field]: {
            ...this.state.formFields[field],
            isRequired: true,
            error: false
          }
        }),
        {}
      )
    }

    switch (event.target.value) {
      case 'natural':
        return this.setState({
          ...this.state,
          wantsInvoiceNatural: true,
          wantsInvoiceLegal: false,
          formFields: {
            ...mapFormFieldValueToState(event.target.name, event.target.value),
            ...setRequiredFields([
              'invoiceName',
              'invoiceFirstSurname',
              'invoiceEmail',
              'nif'
            ])
          }
        })

      case 'legal':
        return this.setState({
          ...this.state,
          wantsInvoiceNatural: false,
          wantsInvoiceLegal: true,
          formFields: {
            ...mapFormFieldValueToState(event.target.name, event.target.value),
            ...setRequiredFields(['company', 'invoiceEmail', 'nif'])
          }
        })

      default:
        return null
    }
  }

  onInputChange: Function = (event: Object) => {
    this.setState({
      ...this.state,
      formFields: {
        ...this.state.formFields,
        [event.target.name]: {
          ...this.state.formFields[event.target.name],
          value: event.target.value
        }
      }
    })
  }

  handleInputErrors: Function = (formFields: Object) => {
    each(Object.keys(formFields), fieldKey => {
      const field = formFields[fieldKey]

      field.isRequired &&
        this.setState(state => ({
          formFields: {
            ...state.formFields,
            [fieldKey]: {
              ...state.formFields[fieldKey],
              error: !field.isValid(field.value)
            }
          }
        }))
    })
  }

  onSubmit: Function = (event: Object) => {
    event.preventDefault()

    isEmpty(
      filter(
        this.state.formFields,
        field =>
          field.isRequired && (!field.value || !field.isValid(field.value))
      )
    )
      ? this.props.updateInvoiceData(
        this.props.address.id,
        Object.keys(this.state.formFields).reduce(
          (memo, fieldKey) => ({
            ...memo,
            [fieldKey]: this.state.formFields[fieldKey].value
          }),
          {}
        )
      )
      : this.handleInputErrors(this.state.formFields)
  }

  getEditAddressFormUrl () {
    return `/${i18n.language}/account/personal/addresses/edit/${this.props.address.id}?comesFrom=checkout`
  }

  render () {
    const { address } = this.props
    return (
      <Grid>
        {this.props.hasInvoice ? (
          <Card>
            <Grid container direction='column'>
              <Grid item style={styles.marginTop}>
                {address.invoicePersonType === 'natural' && (
                  <Typography>
                    <b>{i18n.t('checkout.invoiceForm.name')}</b>{' '}
                    {address.invoiceName} {address.invoiceFirstSurname}
                  </Typography>
                )}
                {address.invoicePersonType === 'legal' && (
                  <Typography>
                    <b>{i18n.t('checkout.invoiceForm.companyName')}</b>{' '}
                    {address.company}
                  </Typography>
                )}
                <Typography>
                  <b>NIF: </b>
                  {address.nif}
                </Typography>
                <Typography>
                  <b>{i18n.t('checkout.invoiceForm.address')}</b>
                  {address.street} {address.details} {address.postalCode}{' '}
                  {address.city}
                </Typography>
              </Grid>
              <Grid item style={styles.marginTop} md={3}>
                <Button
                  onClick={() =>
                    this.props.navigateTo(this.getEditAddressFormUrl())
                  }
                  type='submit'
                >
                  {i18n.t('checkout.edit')}
                </Button>
              </Grid>
            </Grid>
          </Card>
        ) : (
          <form onSubmit={this.onSubmit} style={styles.marginTop}>
            <AddressInvoiceForm
              wantsInvoiceLegal={this.state.wantsInvoiceLegal}
              wantsInvoiceNatural={this.state.wantsInvoiceNatural}
              company={this.state.formFields.company}
              invoiceEmail={this.state.formFields.invoiceEmail}
              invoiceFirstSurname={this.state.formFields.invoiceFirstSurname}
              invoiceName={this.state.formFields.invoiceName}
              invoicePersonType={this.state.formFields.invoicePersonType}
              invoiceSecondSurname={this.state.formFields.invoiceSecondSurname}
              nif={this.state.formFields.nif}
              onInvoicePersonTypeChange={this.onInvoicePersonTypeChange}
              onInputChange={this.onInputChange}
            />
            <Grid container style={styles.button}>
              <Grid item xs={12} sm={6} />
              <Button onClick={this.onSubmit} type='submit'>
                {i18n.t('account.personalTab.save')}
              </Button>
            </Grid>
          </form>
        )}
      </Grid>
    )
  }
}

export default AddAddressInvoiceForm
